import { useEffect } from 'react';

const faqItems = [
  {
    question: 'What types of healthcare staffing services do you provide?',
    answer: 'We offer a wide range of staffing solutions including Travel Staffing, Local Contracts & Per Diem, Direct Hire, Managed Services, Crisis & Rapid Response Staffing, and Telehealth Support Staffing—designed to meet both short-term and long-term needs.',
  },
  {
    question: 'How quickly can you provide staff in urgent situations?',
    answer: 'Through our Crisis & Rapid Response service, we can deploy qualified professionals within hours, ensuring your facility remains fully supported during emergencies or sudden demand surges.',
  },
  {
    question: 'Do you work with both clinical and non-clinical professionals?',
    answer: 'Yes. We specialize in connecting highly qualified nursing, allied health, and non-clinical professionals with healthcare facilities nationwide.',
  },
  {
    question: 'What values guide your staffing process?',
    answer: 'Our work is built on four core values: Integrity, Compassion, Excellence, and Collaboration—ensuring ethical practices, empathy in service, high standards, and strong partnerships.',
  },
  {
    question: 'Can you support virtual care and telehealth programs?',
    answer: 'Absolutely. Our Telehealth Support Staffing service provides both clinical and administrative staff to help organizations build and scale their virtual care operations.',
  },
];

function injectJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

export function useHomeStructuredData() {
  useEffect(() => {
    // Organization schema
    injectJsonLd('ld-organization', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Digitive Health Inc',
      url: 'https://digitivehealth.com',
      logo: 'https://digitivehealth.com/favicon.png',
      description: 'Healthcare staffing agency connecting qualified nursing, allied health, and non-clinical professionals with healthcare facilities nationwide.',
      sameAs: [
        'https://www.linkedin.com/company/digitivehealth',
        'https://www.facebook.com/people/Digitive-Health-Inc/61581203067403/',
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'sales@digitivehealth.com',
        contactType: 'sales',
        availableLanguage: 'English',
      },
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'US',
      },
    });

    // WebSite with SearchAction
    injectJsonLd('ld-website', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Digitive Health',
      url: 'https://digitivehealth.com',
      description: 'Healthcare staffing solutions - Travel Staffing, Direct Hire, Managed Services, and more.',
    });

    // FAQPage schema
    injectJsonLd('ld-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });

    // MedicalBusiness schema
    injectJsonLd('ld-medical-business', {
      '@context': 'https://schema.org',
      '@type': 'MedicalBusiness',
      name: 'Digitive Health Inc',
      url: 'https://digitivehealth.com',
      description: 'Trusted healthcare staffing partner offering travel nursing, allied health, and non-clinical staffing solutions.',
      priceRange: '$$',
      medicalSpecialty: ['Nursing', 'Allied Health'],
      areaServed: {
        '@type': 'Country',
        name: 'United States',
      },
    });

    return () => {
      removeJsonLd('ld-organization');
      removeJsonLd('ld-website');
      removeJsonLd('ld-faq');
      removeJsonLd('ld-medical-business');
    };
  }, []);
}

export function useJobPostingStructuredData() {
  useEffect(() => {
    injectJsonLd('ld-employer', {
      '@context': 'https://schema.org',
      '@type': 'EmploymentAgency',
      name: 'Digitive Health Inc',
      url: 'https://digitivehealth.com/careers',
      description: 'Browse healthcare job openings including travel nursing, allied health, and non-clinical positions.',
      industry: 'Healthcare Staffing',
      areaServed: {
        '@type': 'Country',
        name: 'United States',
      },
    });

    return () => removeJsonLd('ld-employer');
  }, []);
}

export function useServiceStructuredData() {
  useEffect(() => {
    injectJsonLd('ld-service', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Healthcare Staffing',
      provider: {
        '@type': 'Organization',
        name: 'Digitive Health Inc',
        url: 'https://digitivehealth.com',
      },
      description: 'Comprehensive healthcare staffing services including Travel Staffing, Direct Hire, Managed Services, Crisis Response, and Telehealth Support.',
      areaServed: {
        '@type': 'Country',
        name: 'United States',
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Healthcare Staffing Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Travel Staffing' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Local Contracts & Per Diem' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Direct Hire' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Managed Services' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Crisis & Rapid Response Staffing' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Telehealth Support Staffing' } },
        ],
      },
    });

    return () => removeJsonLd('ld-service');
  }, []);
}
